import { useEffect, useMemo, useState } from "react";
import { Badge, Card, EmptyState, PageHeader, type BadgeProps } from "@pericles/framework-frontend";
import { WORK_STATUSES, type WorkStatus } from "@pericles/shared";

import {
  generateProjectWorkplan,
  fetchProjectWorkplan,
  type ProjectChecklistItem,
  type ProjectWorkplan,
  type WorkPackage,
  type WorkplanStatus,
  updateProjectWorkplan,
} from "../api/projectWorkplanClient";
import { WORK_STATUS_UI, normalizeWorkStatus } from "../domain/statusAdapters";

import { InlineAlert } from "./InlineAlert";

interface ProjectWorkplanPanelProps {
  projectId: string;
}

const STATUS_BADGES: Record<WorkStatus, { label: string; variant: BadgeProps["variant"] }> = WORK_STATUS_UI;

const STATUS_SEQUENCE: readonly WorkStatus[] = WORK_STATUSES;

function renderStatusBadge(status?: WorkStatus): JSX.Element {
  const normalized = normalizeWorkStatus(status);
  if (!normalized || !(normalized in STATUS_BADGES)) {
    return <Badge label="Unknown" variant="default" />;
  }

  const badge = STATUS_BADGES[normalized];
  return <Badge label={badge.label} variant={badge.variant} />;
}

function nextStatus(current?: WorkStatus): WorkStatus {
  const normalized = normalizeWorkStatus(current) ?? STATUS_SEQUENCE[0];
  const index = STATUS_SEQUENCE.indexOf(normalized);
  if (index === -1 || index === STATUS_SEQUENCE.length - 1) return STATUS_SEQUENCE[0];
  return STATUS_SEQUENCE[index + 1];
}

function normalizeWorkplanStatuses(plan: ProjectWorkplan): ProjectWorkplan {
  const normalizeStatus = (status?: WorkStatus): WorkStatus => normalizeWorkStatus(status) ?? STATUS_SEQUENCE[0];

  return {
    ...plan,
    workPackages: plan.workPackages.map((pkg) => ({
      ...pkg,
      status: normalizeStatus(pkg.status),
      checklist: pkg.checklist?.map((item) => ({
        ...item,
        status: normalizeStatus(item.status),
      })),
    })),
    checklist: plan.checklist?.map((item) => ({
      ...item,
      status: normalizeStatus(item.status),
    })),
  };
}

function groupByCategory<T extends { category?: string }>(items: T[]): Array<{ category: string; items: T[] }> {
  const map = new Map<string, T[]>();
  items.forEach((item) => {
    const category = item.category?.trim() || "General";
    const current = map.get(category) ?? [];
    current.push(item);
    map.set(category, current);
  });

  return Array.from(map.entries()).map(([category, groupedItems]) => ({ category, items: groupedItems }));
}

function ChecklistGroup({
  title,
  items,
  onToggle,
}: {
  title: string;
  items: ProjectChecklistItem[];
  onToggle?: (id: string) => void;
}): JSX.Element {
  return (
    <Card title={title}>
      <div className="flex flex-col gap-3">
        {items.map((item) => (
          <div key={item.id} className="flex flex-wrap items-center justify-between gap-3 rounded border p-3">
            <div>
              <div className="font-medium text-slate-900">{item.label}</div>
              {item.owner ? <div className="text-sm text-slate-600">Owner: {item.owner}</div> : null}
              {item.notes ? <div className="text-sm text-slate-600">Notes: {item.notes}</div> : null}
            </div>

            <div className="flex items-center gap-2">
              {renderStatusBadge(item.status)}
              {onToggle ? (
                <button
                  type="button"
                  className="button button-secondary button-sm"
                  onClick={() => onToggle(item.id)}
                >
                  Update status (local)
                </button>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function WorkPackageCard({
  workPackage,
  onToggleStatus,
  onToggleChecklist,
}: {
  workPackage: WorkPackage;
  onToggleStatus?: (id: string) => void;
  onToggleChecklist?: (packageId: string, checklistId: string) => void;
}): JSX.Element {
  return (
    <div className="rounded border p-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-lg font-semibold text-slate-900">{workPackage.title}</div>
          <p className="text-sm text-slate-700">{workPackage.summary}</p>

          {workPackage.relatedRequirements?.length ? (
            <p className="mt-2 text-xs text-slate-600">
              Related requirements: {workPackage.relatedRequirements.join(", ")}
            </p>
          ) : null}
          {workPackage.relatedSpecSections?.length ? (
            <p className="text-xs text-slate-600">
              Related spec sections: {workPackage.relatedSpecSections.join(", ")}
            </p>
          ) : null}
        </div>

        <div className="flex items-center gap-2">
          {renderStatusBadge(workPackage.status)}
          {onToggleStatus ? (
            <button
              type="button"
              className="button button-secondary button-sm"
              onClick={() => onToggleStatus(workPackage.id)}
            >
              Update status (local)
            </button>
          ) : null}
        </div>
      </div>

      {workPackage.checklist?.length ? (
        <div className="mt-3 flex flex-col gap-2">
          <div className="text-sm font-semibold text-slate-800">Checklist</div>
          {workPackage.checklist.map((item) => (
            <div key={item.id} className="flex flex-wrap items-center justify-between gap-3 rounded border p-3">
              <div>
                <div className="text-sm font-medium text-slate-900">{item.label}</div>
                {item.owner ? <div className="text-xs text-slate-600">Owner: {item.owner}</div> : null}
                {item.notes ? <div className="text-xs text-slate-600">Notes: {item.notes}</div> : null}
              </div>

              <div className="flex items-center gap-2">
                {renderStatusBadge(item.status)}
                {onToggleChecklist ? (
                  <button
                    type="button"
                    className="button button-secondary button-sm"
                    onClick={() => onToggleChecklist(workPackage.id, item.id)}
                  >
                    Update status (local)
                  </button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function ProjectWorkplanPanel({ projectId }: ProjectWorkplanPanelProps): JSX.Element {
  const [workplan, setWorkplan] = useState<ProjectWorkplan | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);

  useEffect(() => {
    setWorkplan(null);
    setError(null);
    setSaveState("idle");
    setLastSavedAt(null);
    void loadExistingWorkplan();
  }, [projectId]);

  useEffect(() => {
    if (saveState !== "saved") return undefined;

    const timeout = setTimeout(() => setSaveState("idle"), 2500);
    return () => clearTimeout(timeout);
  }, [saveState]);

  const loadExistingWorkplan = async () => {
    if (!projectId) return;

    setIsLoading(true);
    setError(null);

    try {
      const existing = await fetchProjectWorkplan(projectId);
      if (existing) {
        setWorkplan(normalizeWorkplanStatuses(existing.workplan));
        setLastSavedAt(existing.updatedAt ?? null);
      } else {
        setWorkplan(null);
        setLastSavedAt(null);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load workplan";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const workPackageGroups = useMemo(() => {
    if (!workplan?.workPackages?.length) return [];
    return groupByCategory(workplan.workPackages);
  }, [workplan?.workPackages]);

  const checklistGroups = useMemo(() => {
    if (!workplan?.checklist?.length) return [];
    return groupByCategory(workplan.checklist);
  }, [workplan?.checklist]);

  const persistWorkplan = async (nextWorkplan: ProjectWorkplan) => {
    if (!projectId) return;

    setSaveState("saving");
    setSaveError(null);

    try {
      const normalized = normalizeWorkplanStatuses(nextWorkplan);
      const updated = await updateProjectWorkplan(projectId, normalized);
      setWorkplan(normalizeWorkplanStatuses(updated.workplan));
      setLastSavedAt(updated.updatedAt ?? null);
      setSaveState("saved");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to save workplan";
      setSaveError(message);
      setSaveState("error");
    }
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    setError(null);

    try {
      const result = await generateProjectWorkplan(projectId);
      const normalized = normalizeWorkplanStatuses(result.workplan);
      setWorkplan(normalized);
      await persistWorkplan(normalized);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to generate workplan";
      setError(message);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleToggleWorkPackage = (id: string) => {
    setWorkplan((current) => {
      if (!current) return current;
      const updated = current.workPackages.map((pkg) =>
        pkg.id === id ? { ...pkg, status: nextStatus(pkg.status) } : pkg,
      );
      const next = { ...current, workPackages: updated };
      void persistWorkplan(next);
      return next;
    });
  };

  const handleToggleChecklist = (id: string) => {
    setWorkplan((current) => {
      if (!current?.checklist) return current;
      const updated = current.checklist.map((item) =>
        item.id === id ? { ...item, status: nextStatus(item.status) } : item,
      );
      const next = { ...current, checklist: updated };
      void persistWorkplan(next);
      return next;
    });
  };

  const handleTogglePackageChecklist = (packageId: string, checklistId: string) => {
    setWorkplan((current) => {
      if (!current) return current;
      const updatedPackages = current.workPackages.map((pkg) => {
        if (pkg.id !== packageId || !pkg.checklist) return pkg;
        return {
          ...pkg,
          checklist: pkg.checklist.map((item) =>
            item.id === checklistId ? { ...item, status: nextStatus(item.status) } : item,
          ),
        };
      });
      const next = { ...current, workPackages: updatedPackages };
      void persistWorkplan(next);
      return next;
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <PageHeader title="Workplan" />

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          className="button button-primary"
          disabled={isGenerating}
          onClick={() => void handleGenerate()}
        >
          {isGenerating ? "Generating..." : "Generate workplan with AI"}
        </button>
        <button
          type="button"
          className="button button-secondary"
          onClick={() => void loadExistingWorkplan()}
          disabled={isLoading || isGenerating}
        >
          {isLoading ? "Refreshing…" : "Reload saved workplan"}
        </button>
        <span className="text-sm text-slate-600">
          Status changes below are saved to your project workplan.
        </span>
      </div>

      <div className="flex flex-col gap-2" aria-live="polite">
        {error ? <InlineAlert tone="danger" title={error} /> : null}
        {saveError ? (
          <InlineAlert tone="danger" title="Could not save workplan" description={saveError} />
        ) : null}
        {saveState === "saving" ? (
          <InlineAlert tone="info" title="Saving workplan" description="Applying your changes…" />
        ) : null}
        {saveState === "saved" ? (
          <InlineAlert tone="success" title="Workplan saved" description="Latest changes are stored." />
        ) : null}
      </div>

      {lastSavedAt ? (
        <div className="text-xs text-slate-500">Last saved {new Date(lastSavedAt).toLocaleString()}</div>
      ) : null}

      {isLoading ? (
        <Card>
          <div className="p-4 text-sm text-slate-600">Loading workplan...</div>
        </Card>
      ) : null}

      {!workplan && !isLoading ? (
        <Card>
          <EmptyState
            title="No workplan yet"
            description="Generate a workplan with AI to view grouped work packages and checklists."
          />
        </Card>
      ) : null}

      {workplan?.overview ? (
        <Card title="Overview">
          <p className="text-sm text-slate-700">{workplan.overview}</p>
        </Card>
      ) : null}

      {workplan?.assumptions?.length ? (
        <Card title="Assumptions">
          <ul className="list-disc space-y-1 pl-5 text-sm text-slate-700">
            {workplan.assumptions.map((assumption, index) => (
              <li key={index}>{assumption}</li>
            ))}
          </ul>
        </Card>
      ) : null}

      {workplan?.risks?.length ? (
        <Card title="Risks">
          <ul className="list-disc space-y-1 pl-5 text-sm text-slate-700">
            {workplan.risks.map((risk, index) => (
              <li key={index}>{risk}</li>
            ))}
          </ul>
        </Card>
      ) : null}

      {workPackageGroups.length ? (
        workPackageGroups.map((group) => (
          <Card key={group.category} title={`${group.category} (${group.items.length})`}>
            <div className="flex flex-col gap-4">
              {group.items.map((workPackage) => (
                <WorkPackageCard
                  key={workPackage.id}
                  workPackage={workPackage}
                  onToggleStatus={handleToggleWorkPackage}
                  onToggleChecklist={handleTogglePackageChecklist}
                />
              ))}
            </div>
          </Card>
        ))
      ) : workplan ? (
        <Card>
          <EmptyState title="No work packages" description="The generated workplan has no packages." />
        </Card>
      ) : null}

      {checklistGroups.length ? (
        checklistGroups.map((group) => (
          <ChecklistGroup
            key={group.category}
            title={`${group.category} checklist`}
            items={group.items}
            onToggle={handleToggleChecklist}
          />
        ))
      ) : workplan ? (
        <Card>
          <EmptyState title="No checklist items" description="The generated workplan has no checklist." />
        </Card>
      ) : null}
    </div>
  );
}
